import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { map } from 'rxjs';
import { AuthService } from '@app/core/services/auth.service';
import { loginUrlTree } from '@app/core/utils';

/**
 * CW-13d-admin — cookie-auth route guard.
 *
 * In cookie mode the in-memory `isAuthenticated` signal does NOT prove the
 * cookie is absent — it only proves the profile is not loaded. On a hard
 * page reload `currentUser` is null but the cookie may still be valid. We
 * MUST therefore call `rehydrateUser()` on every cold navigation to ask
 * the server (`GET /admin/auth/me`) whether the session is alive.
 *
 * Fast path: if the user is already loaded, allow without HTTP.
 *
 * When the session is gone, the requested URL travels to the login page as
 * `returnUrl` so deep links (e.g. from the registration alert email) land
 * on the intended page after sign-in.
 */
export const authGuard: CanActivateFn = (_route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (authService.isAuthenticated()) {
    return true;
  }

  return authService.rehydrateUser().pipe(
    map((valid) => valid || loginUrlTree(router, state.url)),
  );
};
